import { Component, ElementRef, EventEmitter, HostListener, Input, Optional, Output, Self, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NgControl } from '@angular/forms';

@Component({
  selector: 'app-dropdownlist',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './dropdownlist.component.html',
  styleUrls: ['./dropdownlist.component.scss'],
  standalone: true,
  imports: [CommonModule]
})

export class DropdownlistComponent implements ControlValueAccessor {

  @Input() placeholder: string = 'Elige una opción';
  @Input() options: Options[] = [];
  @Input() selectedValue!: DropdownValue;

  @Output() selectedValueChange = new EventEmitter<DropdownValue>();

  isOpen = false;
  activeIndex = -1;
  disabled = false;

  // Funciones para ControlValueAccessor
  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(
    private readonly elementRef: ElementRef,
    @Optional() @Self() public readonly control?: NgControl
  ) {
    if (control) {
      control.valueAccessor = this;
    }
  }

  // Implementación de ControlValueAccessor
  writeValue(value: any): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get selectedOption(): Options | undefined {
    return this.options?.find(o => o.value === this.selectedValue);
  }

  toggle() {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
    }
  }

  close() {
    this.isOpen = false;
  }

  select(option: Options) {
    if (this.disabled) return;

    this.selectedValue = option.value;
    this.selectedValueChange.emit(option.value);
    this.onChange(option.value);
    this.onTouched();
    this.close();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  onTriggerKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (this.isOpen && this.activeIndex >= 0) {
          this.select(this.options[this.activeIndex]);
        } else {
          this.open();
        }
        break;
      case ' ':
        event.preventDefault();
        if (!this.isOpen) {
          this.open();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.isOpen) {
          this.move(1);
        } else {
          this.open();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen) {
          this.move(-1);
        } else {
          this.open();
        }
        break;
      case 'Escape':
        this.close();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  open() {
    setTimeout(() => {
      this.isOpen = true;
      const selectedIndex = this.options.findIndex(
        o => o.value === this.selectedValue
      );
      this.activeIndex = Math.max(selectedIndex, 0);
    });
  }

  move(direction: number) {
    const length = this.options.length;
    this.activeIndex = (this.activeIndex + direction + length) % length;
    setTimeout(() => {
      const items =
        this.elementRef.nativeElement.querySelectorAll('.dropdown-option');
      items[this.activeIndex]?.scrollIntoView({
        block: 'nearest'
      });
    });
  }
}

interface Options {
  value: DropdownValue;
  name: string;
}

type DropdownValue = string | number;
